import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { API_BASE_URL } from '../../config';
import './OutwardEntry.css';

const OutwardEntryForm = ({ onEntryCreated }) => {
  const [formData, setFormData] = useState({
    transport_mode: '',
    source: '',
    person_name: '',
    person_mobile: '',
    driver_mobile: '',
    driver_name: '',
    vehicle_number: '',
    vehicle_type: '',
    courier_name: '',
    docket_number: '',
    transport_gr_number: ''
  });
  const [serialNumber, setSerialNumber] = useState('');
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [loading, setLoading] = useState(false);

  const transportModeOptions = [
    'By Hand',
    'By Vehicle',
    'By Courier'
  ];

  const sourceOptions = [
    'Baddi Unit 1',
    'Baddi Unit 2',
    'Baddi Unit 3'
  ];

  useEffect(() => {
    fetchSerialNumber();
  }, []);

  const fetchSerialNumber = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(
        `${API_BASE_URL}/api/outward-entries/next-serial`,
        { headers: { Authorization: `Bearer ${token}` }}
      );
      setSerialNumber(response.data.serial_number);
    } catch (err) {
      setError('Error fetching serial number');
    }
  };

  const fetchDriverByMobile = async (mobile) => {
    if (!mobile || mobile.length < 5) return;
    try {
      const token = localStorage.getItem('token');
      const res = await axios.get(
        `${API_BASE_URL}/api/drivers/${mobile}`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      if (res.data.driver) {
        setFormData(prev => ({
          ...prev,
          driver_name: res.data.driver.name
        }));
      }
    } catch (err) {
      setFormData(prev => ({
        ...prev,
        driver_name: ''
      }));
    }
  };

  const fetchVehicleByNumber = async (number) => {
    if (!number || number.length < 3) return;
    try {
      const token = localStorage.getItem('token');
      const res = await axios.get(
        `${API_BASE_URL}/api/vehicles/${number}`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      if (res.data.vehicle) {
        setFormData(prev => ({
          ...prev,
          vehicle_type: res.data.vehicle.vehicle_type
        }));
      }
    } catch (err) {
      setFormData(prev => ({
        ...prev,
        vehicle_type: ''
      }));
    }
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    // Only allow digits and max 10 digits for mobile fields
    if (name === 'person_mobile' || name === 'driver_mobile') {
      const digitsOnly = value.replace(/\D/g, '').slice(0, 10);
      setFormData(prev => ({
        ...prev,
        [name]: digitsOnly
      }));
      if (name === 'driver_mobile') {
        fetchDriverByMobile(digitsOnly);
      }
      return;
    }
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));

    if (name === 'vehicle_number') {
      fetchVehicleByNumber(value);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');
    setSuccess('');

    try {
      const token = localStorage.getItem('token');
      let payload = {
        serial_number: serialNumber,
        transport_mode: formData.transport_mode,
        source: formData.source,
        docket_number: formData.docket_number,
        transport_gr_number: formData.transport_gr_number
      };
      if (formData.transport_mode === 'By Hand') {
        payload.person_name = formData.person_name;
        payload.person_mobile = formData.person_mobile;
      } else if (formData.transport_mode === 'By Vehicle') {
        payload.driver_mobile = formData.driver_mobile;
        payload.driver_name = formData.driver_name;
        payload.vehicle_number = formData.vehicle_number;
        payload.vehicle_type = formData.vehicle_type;
      } else if (formData.transport_mode === 'By Courier') {
        payload.courier_name = formData.courier_name;
        payload.docket_number = formData.docket_number;
      }
      await axios.post(
        `${API_BASE_URL}/api/outward-entries`,
        payload,
        { headers: { Authorization: `Bearer ${token}` }}
      );
      setSuccess('Entry created successfully!');
      setFormData({
        transport_mode: '',
        source: '',
        person_name: '',
        person_mobile: '',
        driver_mobile: '',
        driver_name: '',
        vehicle_number: '',
        vehicle_type: '',
        courier_name: '',
        docket_number: '',
        transport_gr_number: ''
      });
      fetchSerialNumber();
    } catch (err) {
      setError(err.response?.data?.message || 'Error creating entry');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="outward-entry-form">
      <h2>New Outward Entry</h2>
      {error && <div className="error-message">{error}</div>}
      {success && <div className="success-message">{success}</div>}
      <form onSubmit={handleSubmit}>
        <div className="form-row">
          <div className="form-group">
            <label>Serial Number</label>
            <input
              type="text"
              value={serialNumber}
              disabled
              className="readonly-input"
            />
          </div>
          <div className="form-group">
            <label>Transport Mode</label>
            <select
              name="transport_mode"
              value={formData.transport_mode}
              onChange={handleChange}
              required
            >
              <option value="">Select Mode</option>
              {transportModeOptions.map(option => (
                <option key={option} value={option}>{option}</option>
              ))}
            </select>
          </div>
          <div className="form-group">
            <label>Source</label>
            <select
              name="source"
              value={formData.source}
              onChange={handleChange}
              required
            >
              <option value="">Select Source</option>
              {sourceOptions.map(option => (
                <option key={option} value={option}>{option}</option>
              ))}
            </select>
          </div>
        </div>
        {formData.transport_mode === 'By Hand' && (
          <div className="form-row">
            <div className="form-group">
              <label>Person Name</label>
              <input
                type="text"
                name="person_name"
                value={formData.person_name}
                onChange={handleChange}
                required
                placeholder="Enter person name"
              />
            </div>
            <div className="form-group">
              <label>Person Mobile</label>
              <input
                type="text"
                name="person_mobile"
                value={formData.person_mobile}
                onChange={handleChange}
                required
                placeholder="Enter person mobile"
                pattern="[0-9]{10}"
                maxLength={10}
                inputMode="numeric"
                autoComplete="off"
              />
            </div>
          </div>
        )}
        {formData.transport_mode === 'By Vehicle' && (
          <div className="form-row">
            <div className="form-group">
              <label>Driver Mobile</label>
              <input
                type="text"
                name="driver_mobile"
                value={formData.driver_mobile}
                onChange={handleChange}
                required
                placeholder="Enter driver mobile"
                pattern="[0-9]{10}"
                maxLength={10}
                inputMode="numeric"
                autoComplete="off"
              />
            </div>
            <div className="form-group">
              <label>Driver Name</label>
              <input
                type="text"
                name="driver_name"
                value={formData.driver_name}
                onChange={handleChange}
                required
                placeholder="Enter driver name"
              />
            </div>
            <div className="form-group">
              <label>Vehicle Number</label>
              <input
                type="text"
                name="vehicle_number"
                value={formData.vehicle_number}
                onChange={handleChange}
                required
                placeholder="Enter vehicle number"
              />
            </div>
            <div className="form-group">
              <label>Vehicle Type</label>
              <input
                type="text"
                name="vehicle_type"
                value={formData.vehicle_type}
                onChange={handleChange}
                required
                placeholder="Enter vehicle type"
              />
            </div>
          </div>
        )}
        {formData.transport_mode === 'By Courier' && (
          <div className="form-row">
            <div className="form-group">
              <label>Courier Name</label>
              <input
                type="text"
                name="courier_name"
                value={formData.courier_name}
                onChange={handleChange}
                required
                placeholder="Enter courier name"
              />
            </div>
            <div className="form-group">
              <label>Docket Number</label>
              <input
                type="text"
                name="docket_number"
                value={formData.docket_number}
                onChange={handleChange}
                required
                placeholder="Enter docket number"
              />
            </div>
          </div>
        )}
        <div className="form-group" style={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
          <label style={{ marginBottom: 4, textAlign: 'center', width: '100%' }}>Transport GR Number</label>
          <input
            type="text"
            name="transport_gr_number"
            value={formData.transport_gr_number}
            onChange={handleChange}
            placeholder="Enter Transport GR Number"
            style={{ width: '100%' }}
            required
          />
        </div>
        <div className="button-group">
          <button
            type="submit"
            className="submit-button"
            disabled={loading}
          >
            {loading ? 'Creating...' : 'Create Entry'}
          </button>
        </div>
      </form>
    </div>
  );
};

export default OutwardEntryForm;
